import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { map, take, catchError, switchMap } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/compat/auth';

@Injectable({
  providedIn: 'root'
})
export class AppTokenValidationGuard implements CanActivate {

  constructor(
    private afAuth: AngularFireAuth,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    
    return this.afAuth.authState.pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          // User not authenticated, redirect to login
          this.router.navigate(['/login']);
          return of(false);
        }
        
        // User is authenticated, now verify the token is valid for this specific app
        return from(user.getIdTokenResult().then(idTokenResult => {
          // Check token claims to ensure it's valid for this app
          const claims = idTokenResult.claims;
          
          // You can add specific validation here based on your app requirements
          // For example, check if the token has the correct app ID or permissions
          
          // Example validation - check if token has a specific claim for this app
          // If you have a specific claim like 'appName' that should be 'interno'
          if (claims['appName'] && claims['appName'] !== 'interno') {
            console.warn('Token is valid but not for this app:', claims['appName']);
            // Force logout and redirect to login
            this.afAuth.signOut().then(() => {
              this.router.navigate(['/login']);
            });
            return false;
          }
          
          // If no specific app claim exists yet, you might want to check other properties
          // such as email domain, user roles, etc.
          
          return true;
        }).catch(error => {
          console.error('Error validating token:', error);
          // Force logout on token validation error
          this.afAuth.signOut().then(() => {
            this.router.navigate(['/login']);
          });
          return false;
        }));
      }),
      catchError(error => {
        console.error('Error in AppTokenValidationGuard:', error);
        // If we get a Firebase permission error, it's likely because the user is logged out
        // or the token is invalid. Redirect to login page.
        if (error?.code === 'permission-denied' || error?.message?.includes('Missing or insufficient permissions')) {
          this.afAuth.signOut().then(() => {
            // Clear any stored credentials
            localStorage.removeItem('user');
            sessionStorage.clear();
            // Navigate to login
            this.router.navigate(['/login']);
          });
        }
        return of(false);
      })
    );
  }
}
